import { useQuery } from '@tanstack/react-query'
import { SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toApiError } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Card } from '@/components/ui/Card'
import { publicAdmissionsQuery } from '@/features/admissions/api'
import { AdmissionClosed } from '@/features/admissions/components/AdmissionOutcome'
import { AdmissionWizard } from '@/features/admissions/components/AdmissionWizard'
import { PublicAdmissionShell } from '@/features/admissions/components/PublicAdmissionShell'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** /inscription on a school's address: the online application (outside the portals). */
export function PublicAdmissionPage() {
  const { t } = useTranslation('admissions')
  useDocumentTitle('Online admission')
  const info = useQuery(publicAdmissionsQuery())
  const data = info.data
  const title = data?.academicYear
    ? t('Admission {{year}}', { year: data.academicYear.name })
    : t('Online admission')

  return (
    <PublicAdmissionShell
      school={data?.school ?? null}
      title={title}
      subtitle={data?.open && data.introText ? <p className="whitespace-pre-line">{data.introText}</p> : null}
    >
      {info.isPending ? (
        <Card>
          <LoadingState />
        </Card>
      ) : info.error ? (
        <Card>
          {toApiError(info.error).status === 404 ? (
            <EmptyState
              icon={<SearchX />}
              title={t('Online admissions are not available here')}
              description={t('This address does not offer online admissions. Contact the school office.')}
              action={
                <Link to="/login" className="text-sm font-medium text-brand-700 hover:underline">
                  {t('Sign in')}
                </Link>
              }
            />
          ) : (
            <ErrorState onRetry={() => void info.refetch()} />
          )}
        </Card>
      ) : data?.open ? (
        <AdmissionWizard info={data} onClosed={() => void info.refetch()} />
      ) : data ? (
        <AdmissionClosed info={data} />
      ) : null}
    </PublicAdmissionShell>
  )
}
